import React, {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react'

import {
  OutletMenuItemGroupTree,
  OutletStackedMenuItemGroupTree,
} from '@src/pages/OutletPage/menuItemGroupTreeType'

import { useBreakpoint } from '@src/hooks/useBreakpoint'
import {
  OutletFulfilmentStateType,
  UseOutletFulfilment,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import { useMenuItemQueryParam } from '@src/hooks/useMenuItemQueryParam'

type OutletPageContextType = {
  isStackedMenu: boolean
  outletFulfilment: UseOutletFulfilment

  // Modal data
  treeForModalData: OutletStackedMenuItemGroupTree | OutletMenuItemGroupTree
  setTreeForModalData: (
    tree: OutletStackedMenuItemGroupTree | OutletMenuItemGroupTree
  ) => void
  modalMenuItemId: string | null | undefined
  setModalMenuItemQueryParam: (
    id: string | null | undefined,
    navigationType?: 'replaceIn'
  ) => void

  // Search state
  searchTerm: string
  setSearchTerm: (term: string) => void
  menuSearchIds: string[]
  setMenuSearchIds: (ids: string[]) => void
  showMenuSearchResults: boolean
  setShowMenuSearchResults: (show: boolean) => void

  // Navigation state
  activeMenuId: string | null
  setActiveMenuId: (id: string | null) => void
  manualNavigation: boolean
  setManualNavigation: (manual: boolean) => void

  // UI state
  headerHeight: number | null
  setHeaderHeight: (height: number | null) => void
  isCollapsingDivVisible: boolean
  setIsCollapsingDivVisible: (visible: boolean) => void
}

const OutletPageContext = createContext<OutletPageContextType | undefined>(
  undefined
)

export const OutletPageProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isTabletOrGreater } = useBreakpoint()
  const outletFulfilment = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })

  // Modal state
  const [treeForModalData, setTreeForModalData] = useState<
    OutletStackedMenuItemGroupTree | OutletMenuItemGroupTree
  >([])
  const [modalMenuItemId, setModalMenuItemQueryParam] = useMenuItemQueryParam()

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [menuSearchIds, setMenuSearchIds] = useState<string[]>([])
  const [showMenuSearchResults, setShowMenuSearchResults] = useState(false)

  // Navigation state
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  const [manualNavigation, setManualNavigation] = useState(false)

  // UI state
  const [headerHeight, setHeaderHeight] = useState<number | null>(null)
  const [isCollapsingDivVisible, setIsCollapsingDivVisible] = useState(false)

  const restaurant = outletFulfilment.outlet.restaurant
  const isStackedMenu = Boolean(
    restaurant.enableStackedMenu && !isTabletOrGreater
  )

  return (
    <OutletPageContext.Provider
      value={{
        isStackedMenu,
        outletFulfilment,

        treeForModalData,
        setTreeForModalData,
        modalMenuItemId,
        setModalMenuItemQueryParam,

        searchTerm,
        setSearchTerm,
        menuSearchIds,
        setMenuSearchIds,
        showMenuSearchResults,
        setShowMenuSearchResults,

        activeMenuId,
        setActiveMenuId,
        manualNavigation,
        setManualNavigation,

        headerHeight,
        setHeaderHeight,
        isCollapsingDivVisible,
        setIsCollapsingDivVisible,
      }}
    >
      {children}
    </OutletPageContext.Provider>
  )
}

export const useOutletPageContext = () => {
  const context = useContext(OutletPageContext)
  if (context === undefined) {
    throw new Error('useOutletPage must be used within an OutletPageProvider')
  }
  return context
}
